/**
 * App Initializer Component
 *
 * Handles app startup tasks:
 * - Cleaning up expired options
 * - Auto-syncing connected accounts on first login of the day
 *
 * This component renders nothing but runs initialization logic once per session.
 */

import { toEasternDate } from '@luminaria/shared';
import { useEffect, useRef, useState } from 'react';
import { useAuth } from '@shared/auth/AuthContext';
import { useAutoSyncContext } from '@shared/contexts/AutoSyncContext';
import { useCleanupExpiredOptions } from '@shared/hooks/useHoldings';
import { useDailySync } from '@shared/hooks/useSnaptrade';

const DAILY_SYNC_CLAIM_KEY = 'luminaria:dailySyncTabClaim';
/** Another tab's claim this recent means its daily sync is (or was just) in flight. */
const DAILY_SYNC_CLAIM_TTL_MS = 5 * 60 * 1000;

/**
 * Claim this tab as the one that triggers today's daily sync, so several open
 * tabs don't all fire it. Best-effort: the server's per-user claim is the real
 * guard (SYNC-07); without storage every tab may try, and the server skips all
 * but one.
 */
export function tryClaimDailySyncForTab(now: number = Date.now()): boolean {
  try {
    const previous = Number(window.localStorage.getItem(DAILY_SYNC_CLAIM_KEY));
    if (Number.isFinite(previous) && previous > 0 && now - previous < DAILY_SYNC_CLAIM_TTL_MS) {
      return false;
    }
    window.localStorage.setItem(DAILY_SYNC_CLAIM_KEY, String(now));
    return true;
  } catch {
    return true;
  }
}

/** How often the open app checks whether the (Eastern) day rolled over. */
const DAY_ROLLOVER_CHECK_MS = 60 * 1000;

/**
 * Today's Eastern date, re-read every minute so an app left open overnight
 * re-runs its once-per-day startup tasks after midnight.
 */
function useEasternToday(): string {
  const [today, setToday] = useState(() => toEasternDate(new Date()));
  useEffect(() => {
    const intervalId = setInterval(() => {
      const current = toEasternDate(new Date());
      setToday((previous) => (previous === current ? previous : current));
    }, DAY_ROLLOVER_CHECK_MS);
    return () => clearInterval(intervalId);
  }, []);
  return today;
}

export function AppInitializer() {
  const { isAuthenticated, isLoading } = useAuth();
  // Depend on the stable `mutate` functions — the useMutation result is a new
  // object every render, and listing it re-ran these effects on every
  // mutation state change (the cleanup looped and the daily sync never fired, WEBD-04).
  const { mutate: cleanupExpiredOptions } = useCleanupExpiredOptions();
  const { mutate: runDailySync } = useDailySync();
  const { startAutoSync, endAutoSync, isAutoSyncing } = useAutoSyncContext();
  const today = useEasternToday();

  // The day each task last started, so each runs once per day per session
  const cleanupStartedRef = useRef<string | null>(null);
  const lastSyncCheckDateRef = useRef<string | null>(null);

  // Cleanup expired options on startup (and after midnight if left open).
  // The hook's own localStorage guard keeps it to one request per day.
  useEffect(() => {
    if (!isAuthenticated || isLoading || cleanupStartedRef.current === today) {
      return;
    }

    const timeoutId = setTimeout(() => {
      cleanupStartedRef.current = today;
      cleanupExpiredOptions(undefined, {
        onError: (error) => {
          console.error('[AppInitializer] Failed to cleanup expired options:', error);
        },
      });
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, isLoading, today, cleanupExpiredOptions]);

  // Auto-sync connected accounts on first use of the day
  // This runs on initial load and also after midnight when the app was left open
  useEffect(() => {
    // Skip if not authenticated or still loading
    if (!isAuthenticated || isLoading) {
      return;
    }

    // Skip if already syncing (prevents duplicate triggers while API call is in flight)
    if (isAutoSyncing) {
      return;
    }

    // Skip if we've already attempted the sync today in this session
    if (lastSyncCheckDateRef.current === today) {
      return;
    }

    const timeoutId = setTimeout(() => {
      // One attempt per day per session. A failed attempt is not retried in a
      // loop: the user can sync manually, and the next day (or a reload) retries.
      lastSyncCheckDateRef.current = today;

      if (!tryClaimDailySyncForTab()) {
        // Another tab is running today's check.
        return;
      }

      // Start auto-sync state BEFORE making the API call
      // The UI just needs to know "auto-sync is running" to show spinners
      startAutoSync([]);

      runDailySync(undefined, {
        onError: (error) => {
          console.error('[AppInitializer] Daily sync API error:', error);
        },
        onSettled: () => {
          // Always end auto-sync state when done (success or error)
          endAutoSync();
        },
      });
    }, 1000); // 1 second delay to avoid overwhelming the server on startup

    return () => clearTimeout(timeoutId);
  }, [isAuthenticated, isLoading, isAutoSyncing, today, startAutoSync, endAutoSync, runDailySync]);

  // This component renders nothing
  return null;
}
